import { isActionableTask } from 'services/repositories/tasks/tasks';
import { ConnectionRelationship, RelationshipActionRequired, TaskDTO } from 'support/types';

/**
 * Determines if a relationship requires action from the current partner
 * based on their role (sender/receiver) and the relationship status
 */
export const getActionRequiredForRelationship = (
  relationship: ConnectionRelationship,
  currentPartnerId: string,
): Array<RelationshipActionRequired> => {
  const actionRequired: Array<RelationshipActionRequired> = [];
  const currentPartnerIsSender = relationship.sender_partner?.id === currentPartnerId;

  // READY_FOR_TESTING: Only sender needs to act (send test document)
  if (relationship.status === 'READY_FOR_TESTING' && currentPartnerIsSender) {
    actionRequired.push('READY_FOR_TESTING');
  }

  // HAS_TEST_MESSAGES: Only receiver needs to act (approve test message)
  if (relationship.status === 'HAS_TEST_MESSAGES' && !currentPartnerIsSender) {
    actionRequired.push('HAS_TEST_MESSAGES');
  }

  // IN_REVIEW: Only receiver needs to act (review and approve)
  if (relationship.status === 'IN_REVIEW' && !currentPartnerIsSender) {
    actionRequired.push('IN_REVIEW');
  }

  return actionRequired;
};

/**
 * Aggregates action required statuses from multiple relationships
 */
export const getActionRequiredFromRelationships = (
  relationships: Array<ConnectionRelationship>,
  currentPartnerId: string,
): Array<RelationshipActionRequired> => {
  const allActionRequired = relationships.flatMap((rel) => getActionRequiredForRelationship(rel, currentPartnerId));
  return Array.from(new Set(allActionRequired));
};

/**
 * Determines if TASKS_PENDING relationships require action from the current partner
 * by checking if the current partner is the responsible partner for any pending task
 */
export const getActionRequiredForTasks = (
  relationships: Array<ConnectionRelationship>,
  currentPartnerId: string,
  tasksByRelationshipId: Record<string, Array<TaskDTO>>,
): Array<RelationshipActionRequired> => {
  const actionRequired: Array<RelationshipActionRequired> = [];

  for (const relationship of relationships) {
    if (relationship.status !== 'TASKS_PENDING') {
      continue;
    }

    const tasks = tasksByRelationshipId[relationship.id];
    if (!tasks) {
      continue;
    }

    const actionableTasks = tasks.filter(isActionableTask);
    const isResponsible = actionableTasks.some((task) => task.responsible_partner?.id === currentPartnerId);

    if (isResponsible) {
      actionRequired.push('TASKS_PENDING');
    }
  }

  return Array.from(new Set(actionRequired));
};
